import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { customerLabel, dateOnly, money, parseMoney } from "../lib/format";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Layout";
import {
  Button,
  Card,
  DataState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Notice,
  Select,
  StatTile,
  StatusBadge,
  Table,
} from "../components/ui";

type Receivables = {
  rows: Array<{
    id: string;
    number: string;
    customer: { firstName: string; lastName: string; companyName: string | null };
    issuedAt: string | null;
    total: number;
    balance: number;
    ageDays: number;
  }>;
  buckets: { current: number; d30: number; d60: number; d90: number; over90: number };
  totalOutstanding: number;
};

type Reconciliation = {
  items: Array<{
    id: string;
    kind: string;
    reference: string;
    amount: number | null;
    detail: string;
    status: string;
    createdAt: string;
  }>;
  awaitingBankConfirmation: number;
};

type TrialBalance = {
  rows: Array<{ code: string; name: string; type: string; debit: number; credit: number; balance: number }>;
  debits: number;
  credits: number;
  balanced: boolean;
};

const TABS = [
  "receivables",
  "payments",
  "cash closing",
  "reconciliation",
  "expenses",
  "ledger",
] as const;
type Tab = (typeof TABS)[number];

export function FinancePage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>("receivables");

  return (
    <>
      <PageHeader
        title="Finance"
        description="An operational subledger for job and payment reporting. It is not a general ledger, a tax return or an audited statement."
      />

      <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium capitalize transition ${
              tab === t
                ? "border-teal-600 text-teal-700"
                : "border-transparent text-slate-500 hover:text-ink"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "receivables" && <Receivables />}
      {tab === "payments" && <Payments />}
      {tab === "cash closing" && <CashClosing />}
      {tab === "reconciliation" && can("finance.reconcile") && <ReconciliationQueue />}
      {tab === "reconciliation" && !can("finance.reconcile") && (
        <Notice tone="warn">Your role cannot open the reconciliation queue.</Notice>
      )}
      {tab === "expenses" && <Expenses />}
      {tab === "ledger" && <Ledger />}
    </>
  );
}

function Receivables() {
  const receivables = useQuery({
    queryKey: ["receivables"],
    queryFn: () => api.get<Receivables>("/finance/receivables"),
  });
  const data = receivables.data;

  return (
    <DataState loading={receivables.isLoading} error={receivables.error}>
      {data && (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-5">
            <StatTile label="Current" value={money(data.buckets.current)} />
            <StatTile label="31-60 days" value={money(data.buckets.d30)} />
            <StatTile label="61-90 days" value={money(data.buckets.d60)} tone="warn" />
            <StatTile label="91-120 days" value={money(data.buckets.d90)} tone="warn" />
            <StatTile label="Over 120 days" value={money(data.buckets.over90)} tone="warn" />
          </div>

          <Card title={`Outstanding invoices — ${money(data.totalOutstanding)}`}>
            <DataState empty={data.rows.length === 0} emptyMessage="Nothing outstanding.">
              <Table headers={["Invoice", "Customer", "Issued", "Age", "Total", "Balance"]}>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                    <td className="px-3 py-2">{customerLabel(r.customer)}</td>
                    <td className="px-3 py-2 text-slate-600">{dateOnly(r.issuedAt)}</td>
                    <td className="px-3 py-2 tabular-nums">
                      <span className={r.ageDays > 60 ? "font-medium text-amber-700" : ""}>
                        {r.ageDays}d
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">{money(r.total)}</td>
                    <td className="px-3 py-2 font-medium tabular-nums">{money(r.balance)}</td>
                  </tr>
                ))}
              </Table>
            </DataState>
          </Card>
        </div>
      )}
    </DataState>
  );
}

function ReconciliationQueue() {
  const queryClient = useQueryClient();
  const [resolving, setResolving] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const queue = useQuery({
    queryKey: ["reconciliation"],
    queryFn: () => api.get<Reconciliation>("/finance/reconciliation"),
  });

  const resolve = useMutation({
    mutationFn: (vars: { id: string; note: string }) =>
      api.post(`/finance/reconciliation/${vars.id}/resolve`, { note: vars.note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reconciliation"] });
      setResolving(null);
      setNote("");
    },
  });

  return (
    <DataState loading={queue.isLoading} error={queue.error}>
      <div className="space-y-4">
        <Notice>
          Processor-confirmed receipts are tracked separately from funds received in the bank.{" "}
          <strong>{queue.data?.awaitingBankConfirmation ?? 0}</strong> payment(s) are sitting in
          processor clearing and have not been confirmed at the bank.
        </Notice>

        <Card title="Open exceptions">
          <DataState
            empty={(queue.data?.items.length ?? 0) === 0}
            emptyMessage="Nothing unresolved. Every payment, fee and payout is matched."
          >
            <Table headers={["Kind", "Reference", "Amount", "Detail", "Raised", ""]}>
              {queue.data?.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2">
                    <StatusBadge status={item.status} />
                    <div className="mt-1 text-xs text-slate-500">
                      {item.kind.replace(/_/g, " ")}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{item.reference}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {item.amount === null ? "—" : money(item.amount)}
                  </td>
                  <td className="max-w-sm px-3 py-2 text-xs text-slate-600">{item.detail}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{dateOnly(item.createdAt)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="secondary" onClick={() => setResolving(item.id)}>
                      Resolve
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
          </DataState>
        </Card>
      </div>

      {resolving && (
        <Modal title="Resolve exception" onClose={() => setResolving(null)}>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              resolve.mutate({ id: resolving, note });
            }}
          >
            <ErrorBanner error={resolve.error} />
            <Field
              label="How was this resolved?"
              required
              hint="Recorded against your name. Who resolved it and why is part of the audit trail."
            >
              <Input value={note} onChange={(e) => setNote(e.target.value)} required autoFocus />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setResolving(null)}>
                Cancel
              </Button>
              <Button type="submit" loading={resolve.isPending}>
                Mark resolved
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </DataState>
  );
}

type PaymentRow = {
  id: string;
  provider: string;
  method: string;
  amount: number;
  feeAmount: number;
  amountRefunded: number;
  status: string;
  externalId: string | null;
  receivedAt: string;
  bankConfirmedAt: string | null;
  customer: { firstName: string; lastName: string };
  allocations: Array<{ id: string; amount: number; invoice: { number: string } }>;
  refunds: Array<{ id: string; amount: number; status: string; reason: string }>;
};

/**
 * Payments, refunds and payout confirmation.
 *
 * Processor-confirmed money and bank-received money are shown as two different
 * things, because they are (build guide / 08).
 */
function Payments() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [refunding, setRefunding] = useState<PaymentRow | null>(null);
  const [confirming, setConfirming] = useState<PaymentRow | null>(null);

  const payments = useQuery({
    queryKey: ["payments"],
    queryFn: () => api.get<{ data: PaymentRow[] }>("/payments"),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["payments"] });
    void queryClient.invalidateQueries({ queryKey: ["receivables"] });
    void queryClient.invalidateQueries({ queryKey: ["trial-balance"] });
  };

  return (
    <>
      <Card title="Payments received">
        <DataState
          loading={payments.isLoading}
          error={payments.error}
          empty={payments.data?.data.length === 0}
          emptyMessage="No payments recorded yet."
        >
          <Table
            headers={["Received", "Customer", "Method", "Applied to", "Amount", "Fee", "Funds", ""]}
          >
            {payments.data?.data.map((p) => (
              <tr key={p.id}>
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                  {dateOnly(p.receivedAt)}
                </td>
                <td className="px-3 py-2">{customerLabel(p.customer)}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {p.method.toLowerCase().replace("_", " ")}
                  <span className="ml-1 text-slate-400">({p.provider})</span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">
                  {p.allocations.map((a) => a.invoice.number).join(", ") || "unapplied"}
                </td>
                <td className="px-3 py-2 tabular-nums">
                  {money(p.amount)}
                  {p.amountRefunded > 0 && (
                    <span className="block text-xs text-red-600">
                      -{money(p.amountRefunded)} refunded
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums text-slate-600">
                  {p.feeAmount > 0 ? money(p.feeAmount) : "—"}
                </td>
                <td className="px-3 py-2">
                  {p.provider === "manual" ? (
                    <span className="text-xs text-slate-500">at the counter</span>
                  ) : p.bankConfirmedAt ? (
                    <span className="text-xs font-medium text-teal-700">in the bank</span>
                  ) : (
                    <span className="text-xs font-medium text-amber-700">
                      processor clearing
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    {can("finance.reconcile") &&
                      p.provider !== "manual" &&
                      !p.bankConfirmedAt && (
                        <Button variant="secondary" onClick={() => setConfirming(p)}>
                          Confirm payout
                        </Button>
                      )}
                    {can("payments.refund") && p.amountRefunded < p.amount && (
                      <Button variant="secondary" onClick={() => setRefunding(p)}>
                        Refund
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </DataState>
      </Card>

      {refunding && (
        <RefundModal
          payment={refunding}
          onClose={() => setRefunding(null)}
          onDone={() => {
            refresh();
            setRefunding(null);
          }}
        />
      )}
      {confirming && (
        <ConfirmPayoutModal
          payment={confirming}
          onClose={() => setConfirming(null)}
          onDone={() => {
            refresh();
            setConfirming(null);
          }}
        />
      )}
    </>
  );
}

function RefundModal({
  payment,
  onClose,
  onDone,
}: {
  payment: PaymentRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { can } = useAuth();
  const refundable = payment.amount - payment.amountRefunded;
  const [amount, setAmount] = useState((refundable / 100).toFixed(2));
  const [reason, setReason] = useState("");

  // One key per intended refund, fixed when the form opens.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const refund = useMutation({
    mutationFn: () =>
      api.post(`/payments/${payment.id}/refunds`, {
        amount: parseMoney(amount) ?? 0,
        reason,
        idempotencyKey,
      }),
    onSuccess: onDone,
  });

  const cents = parseMoney(amount) ?? 0;
  // Mirrors the server's escalation rule so the operator is not surprised.
  const needsOwner = cents > 50_000;
  const blocked = needsOwner && !can("payments.refund.large");

  return (
    <Modal title="Refund payment" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          refund.mutate();
        }}
      >
        <ErrorBanner error={refund.error} />

        <Notice>
          A refund moves money back through the original provider. It does not reduce the sale. If
          the invoice itself should shrink, issue a credit note as well.
        </Notice>

        <Field label="Amount" required hint={`Up to ${money(refundable)} can still be refunded.`}>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            className="tabular-nums"
            required
          />
        </Field>

        <Field label="Reason" required hint="Recorded in the audit trail against your name.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} required />
        </Field>

        {blocked && (
          <Notice tone="danger">
            A refund above {money(50_000)} needs an owner. Ask an owner to complete this one.
          </Notice>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            loading={refund.isPending}
            disabled={blocked || cents <= 0 || cents > refundable || reason.trim().length < 3}
          >
            Refund {money(cents)}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ConfirmPayoutModal({
  payment,
  onClose,
  onDone,
}: {
  payment: PaymentRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reference, setReference] = useState("");
  const net = payment.amount - payment.feeAmount;

  const confirm = useMutation({
    mutationFn: () =>
      api.post(`/finance/payments/${payment.id}/confirm-bank`, { reference }),
    onSuccess: onDone,
  });

  return (
    <Modal title="Confirm funds received" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          confirm.mutate();
        }}
      >
        <ErrorBanner error={confirm.error} />

        <Notice>
          With no bank feed in this release, an operator confirms the money arrived. This moves{" "}
          {money(net)} from processor clearing to the bank. It is a transfer, not a second sale.
        </Notice>

        <dl className="space-y-1 rounded border border-slate-200 p-3 text-sm">
          <div className="flex justify-between text-slate-600">
            <dt>Gross</dt>
            <dd className="tabular-nums">{money(payment.amount)}</dd>
          </div>
          <div className="flex justify-between text-slate-600">
            <dt>Processor fee</dt>
            <dd className="tabular-nums">-{money(payment.feeAmount)}</dd>
          </div>
          <div className="flex justify-between font-semibold">
            <dt>Expected in the bank</dt>
            <dd className="tabular-nums">{money(net)}</dd>
          </div>
        </dl>

        <Field label="Bank reference" required hint="The statement line or payout id you matched.">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} required />
        </Field>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={confirm.isPending} disabled={!reference.trim()}>
            Confirm receipt
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type CashSession = {
  id: string;
  openingFloat: number;
  countedTotal: number | null;
  expectedTotal: number | null;
  variance: number | null;
  openedAt: string;
  closedAt: string | null;
  note: string | null;
  openedBy: { firstName: string; lastName: string };
  location: { name: string };
};

/**
 * Daily cash closing: opening cash + receipts - refunds - cash expenses,
 * compared with the counted drawer (build guide / 07).
 */
function CashClosing() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [openingFloat, setOpeningFloat] = useState("200.00");
  const [counted, setCounted] = useState("");
  const [note, setNote] = useState("");

  const sessions = useQuery({
    queryKey: ["cash-sessions"],
    queryFn: () => api.get<{ data: CashSession[]; open: CashSession | null }>("/finance/cash-sessions"),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["cash-sessions"] });

  const open = useMutation({
    mutationFn: () =>
      api.post("/finance/cash-sessions", { openingFloat: parseMoney(openingFloat) ?? 0 }),
    onSuccess: refresh,
  });

  const close = useMutation({
    mutationFn: (id: string) =>
      api.post(`/finance/cash-sessions/${id}/close`, {
        countedTotal: parseMoney(counted) ?? 0,
        note: note || undefined,
      }),
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ["reconciliation"] });
      setCounted("");
      setNote("");
    },
  });

  const current = sessions.data?.open ?? null;

  return (
    <DataState loading={sessions.isLoading} error={sessions.error}>
      <div className="space-y-5">
        <Card title={current ? "Open drawer" : "Open the drawer"}>
          <ErrorBanner error={open.error} />
          <ErrorBanner error={close.error} />

          {current ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <StatTile label="Opening float" value={money(current.openingFloat)} />
                <StatTile
                  label="Opened"
                  value={dateOnly(current.openedAt)}
                  sub={`by ${current.openedBy.firstName} ${current.openedBy.lastName}`}
                />
                <StatTile label="Location" value={current.location.name} />
              </div>

              {can("finance.reconcile") && (
                <form
                  className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    close.mutate(current.id);
                  }}
                >
                  <Field
                    label="Counted cash in the drawer"
                    required
                    hint="Count first, then enter. The expected figure is calculated server-side so it cannot be talked into matching."
                  >
                    <Input
                      value={counted}
                      onChange={(e) => setCounted(e.target.value)}
                      inputMode="decimal"
                      placeholder="0.00"
                      className="tabular-nums text-lg"
                      required
                    />
                  </Field>
                  <Field label="Note">
                    <Input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Anything unusual about today"
                    />
                  </Field>
                  <Button type="submit" loading={close.isPending} disabled={!counted}>
                    Close the drawer
                  </Button>
                </form>
              )}
            </div>
          ) : (
            can("finance.reconcile") && (
              <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  open.mutate();
                }}
              >
                <Field label="Opening float" required>
                  <Input
                    value={openingFloat}
                    onChange={(e) => setOpeningFloat(e.target.value)}
                    inputMode="decimal"
                    className="tabular-nums"
                  />
                </Field>
                <Button type="submit" loading={open.isPending}>
                  Open drawer
                </Button>
              </form>
            )
          )}
        </Card>

        <Card title="Closing history">
          <DataState
            empty={(sessions.data?.data.filter((s) => s.closedAt).length ?? 0) === 0}
            emptyMessage="No closed sessions yet."
          >
            <Table
              headers={["Opened", "Closed", "Float", "Expected", "Counted", "Variance", "Note"]}
            >
              {sessions.data?.data
                .filter((s) => s.closedAt)
                .map((s) => (
                  <tr key={s.id}>
                    <td className="px-3 py-2 text-slate-600">{dateOnly(s.openedAt)}</td>
                    <td className="px-3 py-2 text-slate-600">{dateOnly(s.closedAt)}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">
                      {money(s.openingFloat)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">
                      {money(s.expectedTotal)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{money(s.countedTotal)}</td>
                    <td
                      className={`px-3 py-2 tabular-nums font-medium ${
                        (s.variance ?? 0) === 0 ? "text-slate-500" : "text-amber-700"
                      }`}
                    >
                      {money(s.variance)}
                    </td>
                    <td className="max-w-xs truncate px-3 py-2 text-xs text-slate-500">
                      {s.note ?? "—"}
                    </td>
                  </tr>
                ))}
            </Table>
          </DataState>
        </Card>
      </div>
    </DataState>
  );
}

function Expenses() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    category: "Shop supplies",
    vendor: "",
    amount: "",
    isCash: false,
  });

  const expenses = useQuery({
    queryKey: ["expenses"],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string;
          category: string;
          vendor: string;
          amount: number;
          isCash: boolean;
          incurredOn: string;
        }>;
      }>("/finance/expenses"),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post("/finance/expenses", {
        category: form.category,
        vendor: form.vendor,
        amount: parseMoney(form.amount) ?? 0,
        isCash: form.isCash,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["expenses"] });
      setAdding(false);
      setForm({ category: "Shop supplies", vendor: "", amount: "", isCash: false });
    },
  });

  return (
    <>
      <Card
        title="Expenses"
        action={
          can("expenses.write") ? (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              Record expense
            </Button>
          ) : undefined
        }
      >
        <DataState
          loading={expenses.isLoading}
          error={expenses.error}
          empty={expenses.data?.data.length === 0}
          emptyMessage="No expenses recorded."
        >
          <Table headers={["Date", "Category", "Vendor", "Paid from", "Amount"]}>
            {expenses.data?.data.map((e) => (
              <tr key={e.id}>
                <td className="px-3 py-2 text-slate-600">{dateOnly(e.incurredOn)}</td>
                <td className="px-3 py-2">{e.category}</td>
                <td className="px-3 py-2 text-slate-600">{e.vendor}</td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {e.isCash ? "Cash drawer" : "Accounts payable"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{money(e.amount)}</td>
              </tr>
            ))}
          </Table>
        </DataState>
      </Card>

      {adding && (
        <Modal title="Record expense" onClose={() => setAdding(false)}>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <ErrorBanner error={create.error} />
            <Field label="Category" required>
              <Select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                {["Shop supplies", "Rent", "Utilities", "Insurance", "Tools", "Software", "Waste disposal", "Other"].map(
                  (c) => (
                    <option key={c}>{c}</option>
                  ),
                )}
              </Select>
            </Field>
            <Field label="Vendor" required>
              <Input
                value={form.vendor}
                onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
                required
              />
            </Field>
            <Field label="Amount" required>
              <Input
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                inputMode="decimal"
                placeholder="0.00"
                required
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isCash}
                onChange={(e) => setForm((f) => ({ ...f, isCash: e.target.checked }))}
              />
              Paid from the cash drawer (affects daily cash closing)
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Record expense
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

function Ledger() {
  const tb = useQuery({
    queryKey: ["trial-balance"],
    queryFn: () => api.get<TrialBalance>("/finance/trial-balance"),
  });

  return (
    <DataState loading={tb.isLoading} error={tb.error}>
      {tb.data && (
        <Card title="Trial balance">
          {!tb.data.balanced && (
            <div className="mb-3">
              <Notice tone="danger">
                Debits and credits do not match. Stop and investigate before trusting any report on
                this page.
              </Notice>
            </div>
          )}

          <Table headers={["Code", "Account", "Type", "Debit", "Credit", "Balance"]}>
            {tb.data.rows
              .filter((r) => r.debit !== 0 || r.credit !== 0)
              .map((r) => (
                <tr key={r.code}>
                  <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.type.toLowerCase()}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{money(r.debit)}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{money(r.credit)}</td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {money(r.balance)}
                  </td>
                </tr>
              ))}
          </Table>

          <div className="mt-3 flex justify-end gap-6 border-t border-slate-200 pt-3 text-sm font-semibold">
            <span>Debits {money(tb.data.debits)}</span>
            <span>Credits {money(tb.data.credits)}</span>
            <span className={tb.data.balanced ? "text-teal-700" : "text-red-600"}>
              {tb.data.balanced ? "Balanced" : "Out of balance"}
            </span>
          </div>
        </Card>
      )}
    </DataState>
  );
}
